import type { ReactNode } from 'react'
import { Form, FormGroup, FormRow } from '@/components/Form'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
  ModalSheetTitleEmphasis,
} from '@/components/ModalSheet'
import { ErrorBlock } from '@/components/ErrorBlock'
import { ContentLoading } from '@/components/ContentLoading'
import { Button } from '@/components/Button'
import styles from './CorrectionsDialog.module.css'

export interface CorrectionsDialogProps {
  showing: boolean
  onRequestClose?: () => void
  jobName: string
  reasonInput: ReactNode
  isLoadingInitial: boolean
  questions: ReactNode
  saveError?: unknown
  onSaveErrorClose: () => void
  isPending: boolean
  onCancel: () => void
  onSave: () => void
  saveTitle: string
  isSaveDisabled: boolean
}

export function CorrectionsDialog({
  showing,
  onRequestClose,
  jobName,
  reasonInput,
  isLoadingInitial,
  questions,
  saveError,
  onSaveErrorClose,
  isPending,
  onCancel,
  onSave,
  saveTitle,
  isSaveDisabled,
}: CorrectionsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={onRequestClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>
          Make Corrections for{' '}
          <ModalSheetTitleEmphasis>{jobName}</ModalSheetTitleEmphasis>
        </ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <Form testId="corrections-form">
          <ModalSheetSection spaced>
            <FormRow>{reasonInput}</FormRow>
            <div className={styles.separator} />
            <FormRow>
              <FormGroup>
                {isLoadingInitial ? <ContentLoading /> : questions}
              </FormGroup>
            </FormRow>
          </ModalSheetSection>
        </Form>
      </ModalSheetContent>
      {!!saveError && (
        <ErrorBlock
          error={saveError}
          onClose={onSaveErrorClose}
          tackOn="bottom"
        />
      )}
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            isDisabled={isPending}
            onPress={onCancel}
            variant="default"
            testId="corrections-cancel-button"
          />
          <Button
            label="Save"
            onPress={onSave}
            isPending={isPending}
            variant="primary"
            title={saveTitle}
            isDisabled={isPending || isSaveDisabled}
            testId="corrections-save-button"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
